import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { CHILDCARE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { ICONS } from 'Components/utils/icon/icons';
import { getServiceAgesLabel } from 'Components/utils/product/product.utils';
import { t } from 'Src/i18n';
import { ServiceBlock } from 'Components/widgets/service-block/ServiceBlock';
import { PopInReservationBlock } from 'Components/widgets/reservation-block/PopInReservationBlock';
import logger from 'Logger/logger';
import { PopInReservationBlockMorningAfternoonExclusive } from 'Components/widgets/reservation-block/PopInReservationBlockMorningAfternoonExclusive';
import { ChildcareContext } from 'Context/ChildcareContext';
import { TRACK_ACTION_CART_ADD } from 'Tracking/events/cart.tracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import {
  DurationSlotsLabels,
  isPetitClubMed,
  isBabyOrPetitCM,
} from 'Utils/childcare/childcare.utils';
import { CHILDCARE_STOCK_THRESHOLD } from 'Utils/childcare/childcare.consts';
import { getChildcareRemovePriceLabel } from 'Components/widgets/childcare/childcare.helper';
import { UrgentisationChildcareBlock, getMinRemainingStock } from './UrgentisationChildcareBlock';
type Props = {
  blockMinHeight: number;
  childcare: UpsellChildcare;
  childId?: string;
  shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick?: boolean;
  childBirthdateMessage: string;
  className?: string;
  dataCsOverrideId?: string;
  dataTestId?: string;
  isIncluded: boolean;
  onCtaClick?: () => Promise<any> | void;
  openModalWhenRemove?: boolean;
  shouldTriggerChildcareAdvancedReservationBlockOnClick?: boolean;
  type: 'add' | 'remove' | 'disabled' | 'none' | 'edit' | 'full';
  shouldTrack?: boolean;
  trackingType?: string;
  dataSelector?: string;
};
type LastAvailabilityTagProps = {
  childcare: UpsellChildcare;
  type: 'add' | 'remove' | 'disabled' | 'none' | 'edit' | 'full';
};

// tag showing for last 10 availabilities
const LastAvailabilitiesTag = ({ childcare, type }: LastAvailabilityTagProps) => {
  const minRemainingStock = getMinRemainingStock({
    childcare,
  });
  const hasMoreStockThanThreshold =
    isBabyOrPetitCM(childcare) && minRemainingStock > CHILDCARE_STOCK_THRESHOLD;

  if (
    type === 'full' ||
    type === 'remove' ||
    childcare.productInfo.isIncluded ||
    hasMoreStockThanThreshold
  ) {
    return null;
  }

  return (
    <div className="absolute bottom-0 m-10 rounded-pill bg-lightSand px-10 py-5 text-12 font-bold text-orange">
      {t('booking.services.childcare.lastAvailabilities')}
    </div>
  );
};

export const ChildcareServiceBlock = ({
  className,
  dataCsOverrideId,
  isIncluded,
  childcare,
  childId,
  childBirthdateMessage,
  onCtaClick,
  openModalWhenRemove = false,
  type,
  blockMinHeight,
  dataTestId = '',
  shouldTriggerChildcareAdvancedReservationBlockOnClick = false,
  shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick = false,
}: Props) => {
  const {
    isOpen: isChildcareAdvancedReservationBlockOpen,
    onClose: setChildcareBlockAdvancedClosed,
    onOpen: setChildcareAdvancedBlockOpen,
  } = useDisclosure();
  const {
    isOpen: isChildcareMorningAfternoonExclusiveRBOpen,
    onClose: setChildcareMorningAfternoonExclusiveRBClosed,
    onOpen: setChildcareMorningAfternoonExclusiveRBOpen,
  } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const { updateService, deleteAttendeeChildcare } = useContext(ChildcareContext);
  const router = useRouter();
  const { trackClickEvent } = useTrackEvent();
  const track = useTracking();
  // to remove once the comparator is fully migrated to ga4
  const isComparatorView = router.pathname === '/comparator';

  const onClickChildcareButton = () => {
    const currentAttendee = childcare.pricingInfo.attendees?.find(({ id }) => id === childId);

    if (shouldTriggerChildcareAdvancedReservationBlockOnClick) {
      const noRemainingStockAdvanced = currentAttendee?.schedules.every(({ timeSlots }) => {
        return timeSlots.every(({ remainingStock }) => remainingStock === 0);
      });
      return {
        onClick: () => {
          // to remove once the comparator is fully migrated to ga4
          isComparatorView
            ? track({ ...TRACK_ACTION_CART_ADD, labelGA: CHILDCARE_TRACKING.ADD_CHILDCARE })
            : trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
                detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE,
              });
          setChildcareAdvancedBlockOpen();
        },
        ...(noRemainingStockAdvanced && {
          label: t('booking.services.childcare.childcareFull'),
        }),
      };
    }

    if (shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick) {
      return {
        onClick: async () => {
          const currentAttendeeHasRentedSomeSlot = currentAttendee?.schedules?.[0]?.timeSlots?.some(
            ({ isRented }) => isRented,
          );

          if (currentAttendeeHasRentedSomeSlot && !openModalWhenRemove) {
            await deleteAttendeeChildcare({
              attendeeId: childId || '',
              pricingInfo: childcare.pricingInfo,
            });
          } else {
            // to remove once the comparator is fully migrated to ga4
            isComparatorView
              ? track({ ...TRACK_ACTION_CART_ADD, labelGA: CHILDCARE_TRACKING.ADD_CHILDCARE })
              : trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
                  detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE,
                });
            setChildcareMorningAfternoonExclusiveRBOpen();
          }
        },
        ...(type === 'full' && {
          label: t('booking.services.childcare.childcareFull'),
        }),
        onClose: setChildcareMorningAfternoonExclusiveRBClosed,
        isLoading: isLoading,
      };
    }

    if (onCtaClick) {
      return {
        dataCsOverrideId,
        onClick: async () => {
          setIsLoading(true);
          await onCtaClick();
          setIsLoading(false);
        },
        ...(type === 'full' && {
          label: t('booking.services.childcare.childcareFull'),
        }),
      };
    }

    if (type === 'remove' && openModalWhenRemove) {
      return { onClick: () => {} };
    }
  };

  const buttonCfg = onClickChildcareButton();

  const onSubmit = async (updatedService, serviceIds, attendeeIds) => {
    setIsLoading(true);

    try {
      await updateService(updatedService, childcare.pricingInfo, serviceIds, attendeeIds);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const openingHoursLabels = (
    <DurationSlotsLabels
      timeSlots={childcare.productInfo.timeSlots || []}
      closingWeekdays={childcare.productInfo.closingWeekdays || [] || []}
      isPetitCM={isPetitClubMed(childcare)}
    />
  );
  return (
    <div className={className} data-testid={dataTestId}>
      <p
        className="text-16"
        data-testid="ChildcareServiceBlock-childBirthdateMessage"
        style={{
          minHeight: blockMinHeight,
        }}
      >
        {childBirthdateMessage}
      </p>
      <ServiceBlock
        addLabel={t('booking.services.childcare.addChildcare')}
        attendeeId={childId}
        showOnlyCurrentAttendeeSlots
        button={buttonCfg}
        dataSelector="Childcare"
        equipment={{
          id: childcare.pricingInfo.id,
          label: childcare.productInfo.label,
          longDescription: childcare.productInfo.description,
        }}
        header={{
          color: isIncluded ? '#1E2643' : '#924833',
          header: 'large',
          icon: isIncluded ? ICONS.ALL_INCLUSIVE : ICONS.ON_DEMAND,
          label: isIncluded
            ? t('booking.childcarePopIn.includedChildcare')
            : t('booking.childcarePopIn.onDemandChildcare'),
        }}
        image={childcare.productInfo.images[0]}
        includedLabel={t('common.included')}
        intro={!isIncluded ? t('booking.services.childcare.babyClubMedReassurance') : undefined}
        isValidated={['disabled', 'remove', 'full', 'none'].includes(type)}
        lastAvailabilities={<LastAvailabilitiesTag childcare={childcare} type={type} />}
        price={childcare.pricingInfo?.startingPricePerAdult?.formattedValue}
        rentedAmount={getChildcareRemovePriceLabel(childcare, childId)}
        removeLabel={t('booking.services.childcare.removeChildcare')}
        slots={[
          {
            disclaimer: t('booking.services.childcare.openingDisclaimer'),
            icon: ICONS.DURATION,
            label: openingHoursLabels,
            key: 'duration-description',
          },
          {
            icon: ICONS.DATE_STAR,
            label: getServiceAgesLabel(childcare.productInfo.ageInMonths),
            key: 'date-star',
          },
        ].filter((slot) => slot.label)}
        type={type}
        urgentisation={
          childcare.productInfo.isIncluded || childcare.pricingInfo.isFullyPaid ? undefined : (
            <UrgentisationChildcareBlock
              childcare={childcare}
              type={type}
              isByDayChildcare={shouldTriggerChildcareAdvancedReservationBlockOnClick}
              isFullStayHalfDayChildcare={
                shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick
              }
            />
          )
        }
      />
      <div>
        {childcare && isChildcareAdvancedReservationBlockOpen && (
          <PopInReservationBlock
            attendeeId={childId}
            showOnlyCurrentAttendeeSlots
            headerLabel={childcare.productInfo?.label}
            introductionLabel={t('booking.activities.childcare.chooseWhen')}
            onClose={setChildcareBlockAdvancedClosed}
            onSubmit={onSubmit}
            rentButtonLabel={t('booking.common.addPrice')}
            service={childcare.pricingInfo}
          />
        )}
      </div>
      <div>
        {childcare && isChildcareMorningAfternoonExclusiveRBOpen && (
          <PopInReservationBlockMorningAfternoonExclusive
            slots={childcare.pricingInfo.schedules[0].slots}
            headerLabel={childcare.productInfo?.label}
            onClose={setChildcareMorningAfternoonExclusiveRBClosed}
            service={childcare.pricingInfo}
          />
        )}
      </div>
    </div>
  );
};
